import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Card, CardActionArea, CardContent, Skeleton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { SparkLineChart } from '@mui/x-charts/SparkLineChart';
import { chartColors } from '../../theme';
import { formatMonth, formatRupiah } from '../../lib/format';
import { useT } from '../../i18n';

export interface HealthNumbers {
  /** Share of this month's bills paid so far (0..1), and which month that is ("YYYY-MM"). */
  rateToday: number | null;
  rateMonth: string;
  /** Monthly collection rates, oldest first, for the sparkline (0..1). */
  rateTrend: Array<{ month: string; rate: number }>;
  arrearsCents: number | null;
  arrearsHouses: number;
  /** Lived-in units with someone in the app, and how many lived-in units there are. */
  registeredUnits: number | null;
  livedInUnits: number;
  pendingBookings: number | null;
  newComplaints: number | null;
}

function Tile({
  label,
  value,
  sub,
  to,
  tone,
  footer,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  to: string;
  tone?: 'error' | 'warning';
  footer?: ReactNode;
}) {
  return (
    <Card
      variant="outlined"
      sx={{ transition: 'box-shadow 200ms ease, transform 200ms ease', '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 } }}
    >
      <CardActionArea component={RouterLink} to={to} sx={{ height: '100%' }}>
        <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
          <Typography
            variant="h5"
            fontWeight={600}
            // Five tiles share one row from lg up, so the number shrinks a little
            // on narrower screens rather than wrapping "Rp 28.000.000".
            sx={{
              mt: 0.5,
              fontVariantNumeric: 'tabular-nums',
              whiteSpace: 'nowrap',
              fontSize: { lg: 'clamp(1.15rem, 1.6vw, 1.5rem)' },
              color: tone ? `${tone}.main` : 'text.primary',
            }}
          >
            {value}
          </Typography>
          <Box sx={{ minHeight: 22 }}>{sub}</Box>
          {footer ? <Box sx={{ mt: 'auto', pt: 1 }}>{footer}</Box> : null}
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

/**
 * Five "how are we doing" numbers, each with its context and where it leads:
 * collection pace, money owed, app adoption, bookings waiting and new complaints.
 */
export function HealthTiles({ n }: { n: HealthNumbers | null }) {
  const t = useT();
  const theme = useTheme();
  const grid = { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' }, gap: 2 } as const;

  if (!n) {
    return (
      <Box sx={grid}>
        {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} variant="rounded" height={128} sx={{ borderRadius: 4.5 }} />)}
      </Box>
    );
  }

  const pct = (x: number) => `${Math.round(x * 100)}%`;
  const adoption = n.registeredUnits !== null && n.livedInUnits > 0 ? n.registeredUnits / n.livedInUnits : null;
  const unregistered = n.registeredUnits !== null ? n.livedInUnits - n.registeredUnits : 0;

  return (
    <Box sx={grid}>
      <Tile
        label={t('dash.health.rate')}
        value={n.rateToday === null ? '—' : pct(n.rateToday)}
        // Which month the number is for: on its own, "87%" reads as any month.
        sub={<Typography variant="caption" color="text.secondary">{t('dash.health.rateMonth', { month: formatMonth(n.rateMonth) })}</Typography>}
        to="/reports"
        footer={n.rateTrend.length > 1 ? (
          // Hover a point for its month and rate.
          <SparkLineChart
            data={n.rateTrend.map((m) => Math.round(m.rate * 100))}
            height={36}
            curve="natural"
            area
            colors={[chartColors(theme.palette.mode).primary]}
            showTooltip
            showHighlight
            xAxis={{ scaleType: 'point', data: n.rateTrend.map((m) => formatMonth(m.month, true)) }}
            valueFormatter={(v) => (v === null ? '' : `${v}%`)}
            yAxis={{ min: Math.max(0, Math.min(...n.rateTrend.map((m) => m.rate)) * 100 - 10), max: 100 }}
          />
        ) : null}
      />
      <Tile
        label={t('dash.health.arrears')}
        value={n.arrearsCents === null ? '—' : `Rp ${formatRupiah(n.arrearsCents)}`}
        tone={n.arrearsCents ? 'error' : undefined}
        sub={n.arrearsCents === null ? null : (
          <Typography variant="caption" color="text.secondary">
            {n.arrearsHouses > 0 ? t('dash.health.arrearsHouses', { count: n.arrearsHouses }) : t('dash.health.noArrears')}
          </Typography>
        )}
        to="/reports"
        footer={<Typography variant="caption" color="primary">{t('dash.health.seeReport')} →</Typography>}
      />
      <Tile
        label={t('dash.health.adoption')}
        value={adoption === null ? '—' : pct(adoption)}
        sub={adoption === null ? null : (
          <Typography variant="caption" color="text.secondary">
            {unregistered > 0 ? t('dash.health.adoptionMissing', { count: unregistered }) : t('dash.health.adoptionAll')}
          </Typography>
        )}
        to="/units?filter=unregistered"
        footer={adoption === null ? null : (
          // A thin bar, so "how far along" reads at a glance.
          <Box sx={{ height: 6, borderRadius: 3, bgcolor: 'action.hover', overflow: 'hidden' }}>
            <Box sx={{ width: pct(adoption), height: '100%', borderRadius: 3, bgcolor: chartColors(theme.palette.mode).primary }} />
          </Box>
        )}
      />
      {/* Bookings and complaints apart: different jobs, often different people. */}
      <Tile
        label={t('dash.health.bookings')}
        value={n.pendingBookings ?? '—'}
        tone={n.pendingBookings ? 'warning' : undefined}
        sub={n.pendingBookings === null ? null : (
          <Typography variant="caption" color="text.secondary">
            {n.pendingBookings > 0 ? t('dash.health.bookingsWaiting') : t('dash.health.nothingWaiting')}
          </Typography>
        )}
        to="/bookings"
        footer={<Typography variant="caption" color="primary">{t('dash.health.handle')} →</Typography>}
      />
      <Tile
        label={t('dash.health.complaints')}
        value={n.newComplaints ?? '—'}
        tone={n.newComplaints ? 'warning' : undefined}
        sub={n.newComplaints === null ? null : (
          <Typography variant="caption" color="text.secondary">
            {n.newComplaints > 0 ? t('dash.health.complaintsWaiting') : t('dash.health.nothingWaiting')}
          </Typography>
        )}
        to="/complaints"
        footer={<Typography variant="caption" color="primary">{t('dash.health.handle')} →</Typography>}
      />
    </Box>
  );
}
